import { useCallback, useEffect, useRef, useState } from "react"

import { ArrowBackIcon } from "@chakra-ui/icons"
import { Box, Button, IconButton } from "@chakra-ui/react"
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom"

import { useAppSelector } from "app/hooks"
import CustomSearch from "components/custom-search-input/customsearch"
import FeeNameTable, { FeeMappingItem } from "components/table/fee-name-table"
import PageSelector from "components/table/page-selector"
import TemplateHeader from "components/template/template-header"
import useToastHook from "hooks/useToastHook/useToastHook"
import { apiClient } from "services/client"
import { CategoryData } from "services/common"
import { UpdateCategoryResponse } from "services/fee-mapping"
import { selectCurrentPmc } from "store/auth/authSlice"

import { ASC, SortOrder } from "../FeeMapping"
import FeeMappingPagination from "../FeeMappingPagination"

const FeeMappingDetailPage = () => {
  const { name } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPmc = useAppSelector(selectCurrentPmc)
  const [feeData, setFeeData] = useState<CategoryData[]>([])
  const [pageSize, setPageSize] = useState<number>(5)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isUpdatedCategoryLoading, setIsUpdatedCategoryLoading] =
    useState<boolean>(false)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof CategoryData | "wsFeeId" | null
    order: SortOrder
  }>({ key: "wsFeeId", order: ASC })
  const [pageStartIndex, setPageStartIndex] = useState<number>(0)
  const [term, setTerm] = useState<string>(searchParams.get("keyword") || "")

  const [updatedCategoryData, setUpdatedCategoryData] = useState<
    FeeMappingItem[]
  >([])
  const displayToast = useToastHook()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // Set a query parameter
    setSearchParams({
      keyword: term,
      noOfRecords: `${pageSize}`, // Convert number to string using template literal
      pageNo: `${pageStartIndex}`, // Convert number to string using template literal
      sortBy: sortConfig.key || "",
      sortDirection: sortConfig?.order,
    })
  }, [setSearchParams, term, pageSize, pageStartIndex, sortConfig])
  const fetchFeeData = useCallback(async () => {
    setIsLoading(true)

    if (currentPmc?.pmc_id) {
      try {
        const response = await apiClient?.getFeeData({
          params: {
            pmcId: currentPmc?.pmc_id || "",
            limit: pageSize,
            pmsFeeName: name || "",
            skip: pageSize * pageStartIndex,
            sortBy: sortConfig.key || "",
            sortDirection: sortConfig?.order,
            keyword: term?.trim(),
          },
        })
        setFeeData(response.data.data)
      } catch (err) {
        console.log(err)
      } finally {
        setIsLoading(false)
      }
    }
  }, [
    currentPmc?.pmc_id,
    pageStartIndex,
    sortConfig.key,
    name,
    pageSize,
    sortConfig?.order,
    term,
  ])

  // when user changes pmc_id, redirect to fee-mapping page
  const prevPmcIdRef = useRef<string | undefined>(undefined) // keep track of previous pmc_id
  useEffect(() => {
    if (
      prevPmcIdRef.current && // not first render
      prevPmcIdRef.current !== currentPmc?.pmc_id && // pmc changed
      location.pathname.startsWith("/listings/properties/fee-mapping/detail/")
    ) {
      navigate("/listings/properties/fee-mapping", { replace: true })
    }
    // update previous pmc_id
    prevPmcIdRef.current = currentPmc?.pmc_id
  }, [currentPmc?.pmc_id, location.pathname, navigate])

  useEffect(() => {
    const loadData = async () => {
      await fetchFeeData()
    }
    loadData().catch(console.error)
  }, [fetchFeeData])

  const updateCategoryHandler = async () => {
    if (currentPmc?.pmc_id) {
      setIsUpdatedCategoryLoading(true)
      await apiClient
        .updateFeeCategoryDataByName({
          params: { pmcId: currentPmc?.pmc_id },
          data: {
            blockIds: updatedCategoryData
              .filter((item) => item.blocked)
              .map((item) => item.id),
            unblockIds: updatedCategoryData
              .filter((item) => !item.blocked)
              .map((item) => item.id),
          },
        })
        .then(async (response: UpdateCategoryResponse) => {
          if (response?.data?.apiStatus.toLowerCase() === "success") {
            displayToast({
              message: response?.data?.message || "",
              variant: "success",
            })
          } else {
            displayToast({
              message: response?.data?.errorMessage || "Something went wrong",
              variant: "error",
            })
          }
          setIsUpdatedCategoryLoading(false)
          setUpdatedCategoryData([])
          await fetchFeeData()

          return true
        })
        .catch((e: { message: string; errorMessage: string }) => {
          displayToast({
            message:
              e?.errorMessage ||
              e?.message ||
              "Something went wrong! Please try again.",
            variant: "error",
          })

          setIsUpdatedCategoryLoading(false)
        })
    }
  }

  const goBack = () => {
    navigate("/listings/properties/fee-mapping")
  }

  return (
    <div>
      {" "}
      <Box>
        <Box display='flex'>
          <IconButton
            mr={5}
            size='sm'
            variant='outline'
            aria-label='Subtract'
            icon={<ArrowBackIcon h='30px' w='30px' />}
            data-testid='back'
            onClick={goBack}
          />
          <TemplateHeader text={name || ""} />
        </Box>
        <Box flexGrow={1} maxW={{ base: "full", sm: "320px" }} my={2.5}>
          <CustomSearch
            width='full'
            term={term}
            setTerm={(e: string) => {
              setTerm(e)
              setPageStartIndex(0)
            }}
            onClear={() => setTerm("")}
          />
        </Box>
        <Box>
          <FeeNameTable
            categoryData={feeData}
            isLoading={isLoading}
            pageSize={pageSize}
            setUpdatedCategoryData={setUpdatedCategoryData}
            onSort={setSortConfig}
          />
          <Box display='flex' flexDirection='column' gap={4} mt={4}>
            <Box
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              gap={4}
            >
              <PageSelector
                pageSize={pageSize}
                setPageSize={setPageSize}
                setPageStartIndex={setPageStartIndex}
              />

              <FeeMappingPagination
                pageSize={pageSize}
                itemsLength={feeData.length}
                pageStartIndex={pageStartIndex}
                setPageStartIndex={setPageStartIndex}
              />
            </Box>
            <Button
              width='10%'
              height='40px'
              alignSelf='flex-end'
              onClick={updateCategoryHandler}
              disabled={
                updatedCategoryData.length === 0 || isUpdatedCategoryLoading
              }
              isLoading={isUpdatedCategoryLoading}
              mb='20px'
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default FeeMappingDetailPage
